<div class="min-h-screen p-6 md:p-10 space-y-6 bg-primary-50">

  <!-- Header -->
  <header class="flex items-center gap-4">
    <button type="button"
            (click)="back()"
            class="inline-flex items-center justify-center w-20 h-20 rounded-xl border border-primary-200 hover:bg-gray-50 focus:outline-none focus:ring"
            aria-label="Back to Manage">
      <i class="pi pi-arrow-left !text-5xl"></i>
    </button>
    <div class="flex-1">
      <h1 class="text-2xl md:text-3xl font-bold tracking-tight">Manage Attachments</h1>
      <p class="text-sm text-gray-500">
        {{ rows.length }} attachment{{ rows.length !== 1 ? 's' : '' }} across all notes
      </p>
    </div>
    <button type="button"
            (click)="loadAttachments()"
            [disabled]="loading"
            class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring disabled:opacity-50">
      <i class="pi" [class.pi-refresh]="!loading" [class.pi-spin]="loading" [class.pi-spinner]="loading"></i>
      <span class="hidden sm:inline text-sm">Refresh</span>
    </button>
  </header>

  <!-- Search -->
  <div class="relative max-w-sm">
    <i class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
    <input pInputText
           type="text"
           [(ngModel)]="searchText"
           (ngModelChange)="applyFilter()"
           placeholder="Search by filename, note, Space or owner…"
           class="w-full !pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring" />
  </div>

  <!-- Loading spinner -->
  <div *ngIf="loading" class="flex justify-center items-center py-20">
    <i class="pi pi-spin pi-spinner text-4xl text-primary-400"></i>
  </div>

  <!-- Empty state -->
  <div *ngIf="!loading && rows.length === 0"
       class="flex flex-col items-center gap-3 py-20 text-gray-400">
    <i class="pi pi-paperclip text-5xl"></i>
    <p>No attachments found across your notes.</p>
  </div>

  <div *ngIf="!loading && rows.length > 0 && filteredRows.length === 0"
       class="flex flex-col items-center gap-3 py-20 text-gray-400">
    <i class="pi pi-search text-5xl"></i>
    <p>No attachments match your search.</p>
  </div>

  <!-- Table -->
  <div *ngIf="!loading && filteredRows.length > 0"
       class="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
    <table class="w-full table-fixed text-sm">
      <thead>
        <tr class="bg-gray-50 border-b border-gray-200">
          <!-- icon column -->
          <th class="pl-2 pr-1 py-1.5 w-8"></th>

          <th class="px-2 py-1.5 text-left font-semibold text-gray-700 cursor-pointer select-none"
              (click)="sortBy('filename')">
            <div class="flex items-center gap-1 whitespace-nowrap">
              Filename
              <i class="pi text-gray-400 text-xs"
                 [class.pi-sort-amount-up-alt]="sortColumn === 'filename' && sortDir === 'asc'"
                 [class.pi-sort-amount-down-alt]="sortColumn === 'filename' && sortDir === 'desc'"
                 [class.pi-sort-alt]="sortColumn !== 'filename'"></i>
            </div>
          </th>

          <th class="px-2 py-1.5 text-left font-semibold text-gray-700 cursor-pointer select-none w-20 hidden sm:table-cell"
              (click)="sortBy('size')">
            <div class="flex items-center gap-1 whitespace-nowrap">
              Size
              <i class="pi text-gray-400 text-xs"
                 [class.pi-sort-amount-up-alt]="sortColumn === 'size' && sortDir === 'asc'"
                 [class.pi-sort-amount-down-alt]="sortColumn === 'size' && sortDir === 'desc'"
                 [class.pi-sort-alt]="sortColumn !== 'size'"></i>
            </div>
          </th>

          <th class="px-2 py-1.5 text-left font-semibold text-gray-700 cursor-pointer select-none"
              (click)="sortBy('noteTitle')">
            <div class="flex items-center gap-1 whitespace-nowrap">
              Note
              <i class="pi text-gray-400 text-xs"
                 [class.pi-sort-amount-up-alt]="sortColumn === 'noteTitle' && sortDir === 'asc'"
                 [class.pi-sort-amount-down-alt]="sortColumn === 'noteTitle' && sortDir === 'desc'"
                 [class.pi-sort-alt]="sortColumn !== 'noteTitle'"></i>
            </div>
          </th>

          <!-- Date / Space / Owner: own columns from md up; below that they're a second line under the note -->
          <th class="px-2 py-1.5 text-left font-semibold text-gray-700 cursor-pointer select-none w-32 hidden md:table-cell"
              (click)="sortBy('createdOn')">
            <div class="flex items-center gap-1 whitespace-nowrap">
              Date
              <i class="pi text-gray-400 text-xs"
                 [class.pi-sort-amount-up-alt]="sortColumn === 'createdOn' && sortDir === 'asc'"
                 [class.pi-sort-amount-down-alt]="sortColumn === 'createdOn' && sortDir === 'desc'"
                 [class.pi-sort-alt]="sortColumn !== 'createdOn'"></i>
            </div>
          </th>

          <th class="px-2 py-1.5 text-left font-semibold text-gray-700 cursor-pointer select-none w-36 hidden md:table-cell"
              (click)="sortBy('spaceName')">
            <div class="flex items-center gap-1 whitespace-nowrap">
              Space
              <i class="pi text-gray-400 text-xs"
                 [class.pi-sort-amount-up-alt]="sortColumn === 'spaceName' && sortDir === 'asc'"
                 [class.pi-sort-amount-down-alt]="sortColumn === 'spaceName' && sortDir === 'desc'"
                 [class.pi-sort-alt]="sortColumn !== 'spaceName'"></i>
            </div>
          </th>

          <th class="px-2 py-1.5 text-left font-semibold text-gray-700 cursor-pointer select-none w-28 hidden md:table-cell"
              (click)="sortBy('owner')">
            <div class="flex items-center gap-1 whitespace-nowrap">
              Owner
              <i class="pi text-gray-400 text-xs"
                 [class.pi-sort-amount-up-alt]="sortColumn === 'owner' && sortDir === 'asc'"
                 [class.pi-sort-amount-down-alt]="sortColumn === 'owner' && sortDir === 'desc'"
                 [class.pi-sort-alt]="sortColumn !== 'owner'"></i>
            </div>
          </th>

          <!-- actions -->
          <th class="px-1 py-1.5 w-12 sm:w-10"></th>
        </tr>
      </thead>

      <tbody>
        <tr *ngFor="let row of filteredRows"
            class="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">

          <!-- file type icon -->
          <td class="pl-2 pr-1 py-1">
            <i [class]="fileIcon(row.mimeType) + ' text-lg ' + fileIconColor(row.mimeType)"></i>
          </td>

          <!-- filename: an image opens in a new window, other files download -->
          <td class="px-2 py-1 font-medium">
            <a href="#"
               class="block truncate text-gray-800 hover:text-blue-700 hover:underline focus:outline-none focus-visible:underline"
               (click)="openOrDownload(row, $event)"
               [pTooltip]="(isImage(row) ? 'Open ' : 'Download ') + row.filename"
               tooltipPosition="top">
              {{ row.filename }}
            </a>
            <span class="sm:hidden block truncate text-xs text-gray-500 font-normal">{{ formatSize(row.size) }}</span>
          </td>

          <!-- size -->
          <td class="px-2 py-1 text-gray-500 whitespace-nowrap hidden sm:table-cell">
            {{ formatSize(row.size) }}
          </td>

          <!-- note title (clickable, opens in new tab) -->
          <td class="px-2 py-1">
            <a [routerLink]="['/view', row.noteId]"
               target="_blank"
               rel="noopener"
               class="block truncate text-left text-blue-600 hover:text-blue-800 hover:underline focus:outline-none"
               [pTooltip]="row.noteTitle"
               tooltipPosition="top">
              {{ row.noteTitle }}
            </a>
            <span class="md:hidden block text-xs text-gray-500 break-words">
              {{ formatDate(row.createdOn) }} · {{ row.spaceName }}<ng-container *ngIf="row.owner"> · {{ row.owner }}</ng-container>
            </span>
          </td>

          <!-- date -->
          <td class="px-2 py-1 text-gray-500 whitespace-nowrap hidden md:table-cell">
            {{ formatDate(row.createdOn) }}
          </td>

          <!-- space -->
          <td class="px-2 py-1 text-gray-700 hidden md:table-cell">
            <span class="block truncate" [pTooltip]="row.spaceName" tooltipPosition="top">{{ row.spaceName }}</span>
          </td>

          <!-- owner -->
          <td class="px-2 py-1 text-gray-700 hidden md:table-cell">
            <span class="block truncate">{{ row.owner || '—' }}</span>
          </td>

          <!-- menu button -->
          <td class="px-1 py-1 text-right">
            <button type="button"
                    (click)="openMenu($event, row)"
                    class="inline-flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
                    aria-label="Actions">
              <i class="pi pi-ellipsis-v"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Shared popup menu -->
  <p-menu #rowMenu [model]="menuItems" [popup]="true" appendTo="body"></p-menu>

  <!-- Rename dialog -->
  <p-dialog [(visible)]="showRenameDialog"
            header="Rename Attachment"
            [modal]="true"
            [style]="{ width: 'min(480px, 95vw)' }"
            [draggable]="false"
            [closable]="true"
            appendTo="body">
    <div class="space-y-4 py-2">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">New filename</label>
        <input pInputText
               type="text"
               [(ngModel)]="renameValue"
               class="w-full"
               placeholder="Enter new filename"
               (keydown.enter)="submitRename()" />
      </div>
      <p *ngIf="renameErrorMsg" class="text-sm text-red-600">{{ renameErrorMsg }}</p>
    </div>

    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2 pt-3 border-t border-gray-200">
        <button pButton
                type="button"
                label="Cancel"
                class="p-button-text"
                [disabled]="renaming"
                (click)="showRenameDialog = false"></button>
        <button pButton
                type="button"
                label="Rename"
                [loading]="renaming"
                (click)="submitRename()"></button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Confirm dialog -->
  <p-confirmDialog appendTo="body"></p-confirmDialog>

</div>
